import type { MarketVariants } from '@/lib/i18n'

export type Slide6TimeToReactCopy = {
  /** Phone notification mockup. */
  alertTitle: string
  alertNow: string
  /** "{amount}" and "{retailer}" are filled from the two fields below. */
  alertBody: string
  /** Card payment alert example. Currency + retailer name vary by market. */
  alertAmount: string
  alertRetailer: string

  title: string
  body: string
  guideButton: string

  /** Alerts guide slideover. */
  guideTitle: string
  closeAria: string
  appHeading: string
  appBody: string
  thresholdHeading: string
  /** "{low}" and "{high}" are filled from thresholdLow / thresholdHigh. */
  thresholdBody: string
  /** Threshold examples in the guide. Currency-prefixed. */
  thresholdLow: string
  thresholdHigh: string
  creditHeading: string
  /** "{account}" is filled from primaryAccountLabel. */
  creditBody: string
  /** Primary account label in the threshold guide ("current account" GB,
   *  "checking account" US/CA, "everyday account" AU). */
  primaryAccountLabel: string
  spotHeading: string
  spotBody: string
}

export const copy: MarketVariants<Slide6TimeToReactCopy> = {
  US: {
    alertTitle: 'Transaction alert',
    alertNow: 'now',
    alertBody: 'Card payment of {amount} at {retailer}.',
    alertAmount: '$184.70',
    alertRetailer: 'ELECTRO MART',

    title: 'Enable transaction alerts.',
    body:
      'Get notified every time money moves. The sooner you spot something wrong, the easier it is to fix.',
    guideButton: 'How to turn alerts on',

    guideTitle: 'How to turn on transaction alerts',
    closeAria: 'Close',
    appHeading: 'Through your banking app',
    appBody:
      'Most banking apps let you turn on notifications for every transaction. Look for “Notifications,” “Alerts,” or “Account settings” in the app. Turn on alerts for card payments, cash withdrawals, bank transfers, and any transaction over a threshold you choose.',
    thresholdHeading: 'Set a low threshold',
    thresholdBody:
      'Some people set alerts for transactions over {low} or {high}. Consider setting it lower, or turning on alerts for every transaction. Fraudsters often start with a small payment to test whether the card works before making larger charges.',
    thresholdLow: '$50',
    thresholdHigh: '$100',
    creditHeading: 'Credit cards too',
    creditBody:
      'Don’t just set alerts on your {account}. Turn them on for credit cards and savings accounts as well. The more visibility you have, the faster you can act if something looks wrong.',
    primaryAccountLabel: 'checking account',
    spotHeading: 'What to do if you spot something',
    spotBody:
      'If you get an alert for a transaction you don’t recognize, call your bank straight away using the number on the back of your card or inside the banking app. The sooner you report it, the better your chances of limiting the damage.',
  },
  GB: {
    alertAmount: '£184.70',
    primaryAccountLabel: 'current account',
    thresholdLow: '£50',
    thresholdHigh: '£100',
  },
  CA: {
    alertAmount: '$184.70',
    primaryAccountLabel: 'chequing account',
    thresholdLow: '$50',
    thresholdHigh: '$100',
  },
  AU: {
    alertAmount: '$184.70',
    primaryAccountLabel: 'everyday account',
    thresholdLow: '$50',
    thresholdHigh: '$100',
  },
  QA: {
    alertAmount: 'QAR 675.50',
    primaryAccountLabel: 'current account',
    thresholdLow: 'QAR 200',
    thresholdHigh: 'QAR 400',
  },
  ar: {
    alertTitle: 'تنبيه معاملة',
    alertNow: 'الآن',
    alertBody: 'دفعة بالبطاقة بقيمة {amount} لدى {retailer}.',
    alertAmount: '675.50 ر.ق',
    alertRetailer: 'إلكترو مارت',

    title: 'فعّل تنبيهات المعاملات.',
    body:
      'تلقَّ إشعاراً في كل مرة يتحرك فيها المال. كلما اكتشفت الخطأ مبكراً، كان إصلاحه أسهل.',
    guideButton: 'كيف تفعّل التنبيهات',

    guideTitle: 'كيفية تفعيل تنبيهات المعاملات',
    closeAria: 'إغلاق',
    appHeading: 'من خلال تطبيق البنك',
    appBody:
      'تتيح لك معظم تطبيقات البنوك تفعيل الإشعارات لكل معاملة. ابحث في التطبيق عن "الإشعارات" أو "التنبيهات" أو "إعدادات الحساب". فعّل التنبيهات لمدفوعات البطاقة والسحب النقدي والتحويلات المصرفية وأي معاملة تتجاوز حداً تختاره بنفسك.',
    thresholdHeading: 'حدّد حداً منخفضاً',
    thresholdBody:
      'يضبط بعض الناس التنبيهات للمعاملات التي تتجاوز {low} أو {high}. فكّر في خفض هذا الحد، أو فعّل التنبيهات لكل معاملة. فغالباً ما يبدأ المحتالون بدفعة صغيرة لاختبار البطاقة قبل تنفيذ عمليات أكبر.',
    thresholdLow: '200 ريال قطري',
    thresholdHigh: '400 ريال قطري',
    creditHeading: 'وبطاقات الائتمان أيضاً',
    creditBody:
      'لا تكتفِ بضبط التنبيهات على {account}. فعّلها أيضاً لبطاقات الائتمان وحسابات التوفير. فكلما زادت متابعتك، أسرعت في التصرف إذا بدا شيء غير طبيعي.',
    primaryAccountLabel: 'حسابك الجاري',
    spotHeading: 'ماذا تفعل إذا لاحظت شيئاً',
    spotBody:
      'إذا وصلك تنبيه بمعاملة لا تعرفها، فاتصل بالبنك فوراً على الرقم المدوّن خلف بطاقتك أو الموجود داخل تطبيق البنك. كلما أبلغت مبكراً، زادت فرصك في الحد من الضرر.',
  },
}
